import { useEffect, useState } from "react";
import { api, AUD, PCT } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const SCENARIO_COLORS = ["#7B8F5C", "#C97B5A", "#4A6B7A", "#A88F6E", "#5F8071", "#8C6E52"];

const DEFAULT_FORM = {
    scenario: "Next Door Property",
    purchase_price: 1650000,
    cash_used: 650000,
    debt: 1000000,
    interest_rate: 0.065,
    loan_years: 25,
    annual_rent: 41600,
    annual_expenses: 12000,
    growth_rate: 0.04,
    holding_period: 10,
};

const FIELDS = [
    { k: "scenario", label: "Scenario name", type: "text" },
    { k: "purchase_price", label: "Purchase price", type: "money" },
    { k: "cash_used", label: "Cash used (deposit + costs)", type: "money" },
    { k: "debt", label: "Debt", type: "money" },
    { k: "interest_rate", label: "Interest rate", type: "pct" },
    { k: "loan_years", label: "Loan years", type: "int" },
    { k: "annual_rent", label: "Annual rent", type: "money" },
    { k: "annual_expenses", label: "Annual expenses", type: "money" },
    { k: "growth_rate", label: "Property growth", type: "pct" },
    { k: "holding_period", label: "Holding period (yrs)", type: "int" },
];

export default function Property() {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    // Persistent multi-scenario comparison: list of {id, name, inputs, result, color}
    const [saved, setSaved] = useState([]);

    // Load previously-saved scenarios on mount
    useEffect(() => {
        api.get("/property/sim-saved").then(r => {
            // Assign a color per scenario based on load order
            const withColor = r.data.map((s, i) => ({
                ...s,
                color: s.color || SCENARIO_COLORS[i % SCENARIO_COLORS.length],
            }));
            setSaved(withColor);
        }).catch(() => {});
    }, []);

    const setField = (k, v, type) => {
        const val = type === "text" ? v
            : type === "pct" ? Number(v) / 100
            : type === "int" ? parseInt(v || 0, 10)
            : Number(v);
        setForm(f => ({ ...f, [k]: val }));
    };

    const displayValue = (k, type) => {
        if (type === "text") return form[k];
        if (type === "pct") return (form[k] * 100).toFixed(2);
        return form[k];
    };

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/property/simulate", form);
            setResult(data);
        } catch {
            toast.error("Simulation failed");
        } finally {
            setBusy(false);
        }
    };

    const addToComparison = async () => {
        const name = (form.scenario || "").trim();
        if (!name) {
            toast.warning("Give the scenario a name first");
            return;
        }
        setBusy(true);
        try {
            const { data: simResult } = await api.post("/property/simulate", form);
            setResult(simResult);
            const color = SCENARIO_COLORS[saved.length % SCENARIO_COLORS.length];
            const { data: savedDoc } = await api.post("/property/sim-saved", {
                name,
                inputs: { ...form },
                result: simResult,
                color,
            });
            setSaved(s => [...s, { ...savedDoc, color }]);
            toast.success(`Saved "${name}" to comparison`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add scenario");
        } finally {
            setBusy(false);
        }
    };

    const removeScenario = async (idx) => {
        const s = saved[idx];
        if (!confirm(`Remove "${s.name}" from comparison?`)) return;
        try {
            await api.delete(`/property/sim-saved/${s.id}`);
            setSaved(list => list.filter((_, i) => i !== idx));
            toast.success(`Removed "${s.name}"`);
        } catch {
            toast.error("Failed to remove");
        }
    };

    const clearAll = async () => {
        if (saved.length === 0) return;
        if (!confirm(`Remove all ${saved.length} scenarios?`)) return;
        try {
            await Promise.all(saved.map(s => api.delete(`/property/sim-saved/${s.id}`)));
            setSaved([]);
            toast.success("Cleared");
        } catch {
            toast.error("Some scenarios failed to delete");
        }
    };

    return (
        <div data-testid="property-page">
            <div className="mb-8">
                <div className="label-eyebrow mb-2">Property Modeller</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Buy or invest?</h1>
                <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--muted)" }}>
                    Model a property purchase over any holding period and compare it
                    against a <em>Buy Nothing</em> baseline: the same cash invested at
                    your Expected Portfolio Return (from Assumptions).
                </p>
            </div>

            <div className="hairline bg-white p-6 rounded mb-8" data-testid="property-form">
                <div className="grid md:grid-cols-5 gap-4 mb-4">
                    {FIELDS.map(f => (
                        <div key={f.k}>
                            <div className="label-eyebrow mb-2">{f.label}</div>
                            <input
                                type={f.type === "text" ? "text" : "number"}
                                step={f.type === "pct" ? "0.01" : "any"}
                                value={displayValue(f.k, f.type)}
                                onChange={e => setField(f.k, e.target.value, f.type)}
                                data-testid={`prop-input-${f.k}`}
                                className={`w-full border border-[var(--line)] px-3 py-2 rounded text-sm ${f.type !== "text" ? "font-mono-num text-right" : ""}`}
                            />
                        </div>
                    ))}
                </div>
                <button className="btn-primary text-sm" onClick={run} disabled={busy}
                        data-testid="prop-run">
                    {busy ? "Simulating…" : "Run scenario"}
                </button>
                <button className="btn-ghost text-sm ml-2 inline-flex items-center gap-1"
                        onClick={addToComparison} disabled={busy}
                        data-testid="prop-add-comparison">
                    <Plus size={14} /> Save + add to comparison
                </button>
            </div>

            {saved.length > 0 && (
                <div className="hairline bg-white p-8 rounded mb-8" data-testid="prop-comparison">
                    <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                        <div>
                            <div className="label-eyebrow">Comparison</div>
                            <h2 className="font-serif text-2xl mt-1">
                                {saved.length} scenario{saved.length === 1 ? "" : "s"} · Property vs Cash Invested
                            </h2>
                            <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                Each mini-chart pits the property (equity + cumulative cash flow)
                                against the same cash invested at your Expected Portfolio Return.
                            </p>
                        </div>
                        <button className="btn-ghost text-xs" onClick={clearAll}
                                data-testid="prop-clear-comparison">
                            Clear all
                        </button>
                    </div>

                    {/* Small multiples: one chart per scenario */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                        {saved.map((s, i) => {
                            const series = s.result.series.map(pt => ({
                                year: pt.year,
                                property: pt.equity + pt.cumulative_cashflow,
                                cash: pt.baseline_value,
                            }));
                            const propWins = s.result.delta_vs_baseline > 0;
                            return (
                                <div key={s.id} className="hairline bg-white p-6 rounded"
                                     data-testid={`prop-mini-${i}`}>
                                    <div className="flex items-baseline justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-block w-3 h-3 rounded-full"
                                                  style={{ background: s.color }} />
                                            <div className="font-serif text-lg">{s.name}</div>
                                        </div>
                                        <span className="chip"
                                              style={{
                                                  background: propWins ? "var(--eucalyptus)" : "var(--terracotta)",
                                                  color: "var(--bone)",
                                              }}>
                                            {propWins ? "Property wins" : "Cash wins"}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3 text-xs">
                                        <div>
                                            <div className="label-eyebrow">Property</div>
                                            <div className="font-mono-num mt-1">
                                                {AUD(s.result.final_equity + s.result.final_cumulative_cashflow)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="label-eyebrow">Cash invested</div>
                                            <div className="font-mono-num mt-1">
                                                {AUD(s.result.series[s.result.series.length - 1].baseline_value)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="label-eyebrow">Delta</div>
                                            <div className="font-mono-num mt-1"
                                                 style={{ color: propWins ? "var(--eucalyptus)" : "var(--terracotta)" }}>
                                                {propWins ? "+" : ""}{AUD(s.result.delta_vs_baseline)}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ height: 200 }}>
                                        <ResponsiveContainer>
                                            <LineChart data={series}>
                                                <CartesianGrid stroke="var(--line)" vertical={false} />
                                                <XAxis dataKey="year" stroke="var(--muted)" tick={{ fontSize: 10 }} />
                                                <YAxis stroke="var(--muted)" tick={{ fontSize: 10 }}
                                                       tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                                                <Tooltip formatter={v => AUD(v)} />
                                                <Legend wrapperStyle={{ fontSize: 10 }} />
                                                <Line type="monotone" dataKey="property" name="Property (equity + cashflow)"
                                                      stroke={s.color} strokeWidth={2.5} dot={{ r: 2 }} />
                                                <Line type="monotone" dataKey="cash" name="Cash invested"
                                                      stroke="#8C6E52" strokeWidth={2} strokeDasharray="4 4"
                                                      dot={{ r: 2 }} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="mt-2 text-right">
                                        <button onClick={() => removeScenario(i)}
                                                className="text-xs text-[var(--muted)] hover:text-[var(--terracotta)] inline-flex items-center gap-1"
                                                data-testid={`prop-mini-del-${i}`}>
                                            <Trash size={12} /> Remove
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="editorial w-full" data-testid="prop-comparison-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Scenario</th>
                                    <th className="num">Cash used</th>
                                    <th className="num">Net cashflow / yr</th>
                                    <th className="num">Gross yield</th>
                                    <th className="num">Property total</th>
                                    <th className="num">Cash invested</th>
                                    <th className="num">Delta</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {saved.map((s, i) => {
                                    const propTotal = s.result.final_equity + s.result.final_cumulative_cashflow;
                                    const cashTotal = s.result.series[s.result.series.length - 1].baseline_value;
                                    return (
                                        <tr key={s.id} data-testid={`prop-comparison-row-${i}`}>
                                            <td>
                                                <span className="inline-block w-3 h-3 rounded-full"
                                                      style={{ background: s.color }} />
                                            </td>
                                            <td className="font-semibold">{s.name}</td>
                                            <td className="num font-mono-num">{AUD(s.inputs.cash_used)}</td>
                                            <td className="num font-mono-num"
                                                style={{ color: s.result.net_annual_cashflow < 0 ? "var(--terracotta)" : "var(--eucalyptus)" }}>
                                                {AUD(s.result.net_annual_cashflow)}
                                            </td>
                                            <td className="num font-mono-num">{PCT(s.result.gross_yield)}</td>
                                            <td className="num font-mono-num font-medium">{AUD(propTotal)}</td>
                                            <td className="num font-mono-num">{AUD(cashTotal)}</td>
                                            <td className="num font-mono-num"
                                                style={{ color: s.result.delta_vs_baseline >= 0 ? "var(--eucalyptus)" : "var(--terracotta)" }}>
                                                {s.result.delta_vs_baseline >= 0 ? "+" : ""}{AUD(s.result.delta_vs_baseline)}
                                            </td>
                                            <td>
                                                <button onClick={() => removeScenario(i)}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                        data-testid={`prop-comparison-del-${i}`}>
                                                    <Trash size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {result && (
                <>
                    <div className="grid md:grid-cols-4 gap-6 mb-8">
                        <div className="hairline bg-white p-6 rounded" data-testid="prop-kpi-cashflow">
                            <div className="label-eyebrow">Net annual cash flow</div>
                            <div className="font-serif text-2xl mt-3 font-mono-num"
                                 style={{ color: result.net_annual_cashflow < 0 ? "var(--terracotta)" : "var(--eucalyptus)" }}>
                                {AUD(result.net_annual_cashflow)}
                            </div>
                        </div>
                        <div className="hairline bg-white p-6 rounded" data-testid="prop-kpi-yield">
                            <div className="label-eyebrow">Gross / Net yield</div>
                            <div className="font-serif text-2xl mt-3 font-mono-num">
                                {PCT(result.gross_yield)} / {PCT(result.net_yield)}
                            </div>
                        </div>
                        <div className="hairline bg-white p-6 rounded" data-testid="prop-kpi-equity">
                            <div className="label-eyebrow">Final equity ({form.holding_period}yr)</div>
                            <div className="font-serif text-2xl mt-3 font-mono-num">{AUD(result.final_equity)}</div>
                        </div>
                        <div className="p-6 rounded"
                             style={{ background: result.outperforms_baseline ? "var(--eucalyptus)" : "var(--terracotta)", color: "var(--bone)" }}
                             data-testid="prop-kpi-delta">
                            <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>
                                Property vs Buy Nothing
                            </div>
                            <div className="font-serif text-2xl mt-3 font-mono-num">
                                {result.delta_vs_baseline >= 0 ? "+" : ""}{AUD(result.delta_vs_baseline)}
                            </div>
                            <div className="text-xs mt-2 opacity-80">
                                {result.outperforms_baseline ? "Property wins" : "Baseline wins"}
                                {result.break_even_year !== null && ` · break-even yr ${result.break_even_year}`}
                            </div>
                        </div>
                    </div>

                    <div className="hairline bg-white p-8 rounded mb-8" data-testid="prop-chart">
                        <div className="mb-4">
                            <div className="label-eyebrow">Comparison</div>
                            <h2 className="font-serif text-2xl mt-1">Property equity + cash flow vs Baseline investment</h2>
                        </div>
                        <div style={{ height: 320 }}>
                            <ResponsiveContainer>
                                <LineChart data={result.series.map(s => ({
                                    year: s.year,
                                    property_wealth: s.equity + s.cumulative_cashflow,
                                    baseline_value: s.baseline_value,
                                }))}>
                                    <CartesianGrid stroke="var(--line)" vertical={false} />
                                    <XAxis dataKey="year" stroke="var(--muted)" tick={{ fontSize: 11 }} />
                                    <YAxis stroke="var(--muted)" tick={{ fontSize: 11 }}
                                           tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip formatter={v => AUD(v)} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="property_wealth" name="Property (equity + cashflow)"
                                          stroke="var(--eucalyptus)" strokeWidth={2.5} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="baseline_value" name="Buy Nothing (invest cash)"
                                          stroke="var(--terracotta)" strokeWidth={2.5}
                                          strokeDasharray="4 4" dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="hairline bg-white p-8 rounded" data-testid="prop-detail-table">
                        <div className="mb-4">
                            <div className="label-eyebrow">Year-by-year</div>
                            <h2 className="font-serif text-2xl mt-1">Cash flow, equity, baseline</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left" style={{ color: "var(--muted)" }}>
                                        <th className="py-2 pr-4 font-normal">Year</th>
                                        <th className="py-2 pr-4 font-normal text-right">Property value</th>
                                        <th className="py-2 pr-4 font-normal text-right">Loan balance</th>
                                        <th className="py-2 pr-4 font-normal text-right">Equity</th>
                                        <th className="py-2 pr-4 font-normal text-right">Cum. cashflow</th>
                                        <th className="py-2 pr-4 font-normal text-right">Baseline</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.series.map(s => (
                                        <tr key={s.year} className="border-t" style={{ borderColor: "var(--line)" }}>
                                            <td className="py-2 pr-4">{s.year}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num">{AUD(s.property_value)}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num">{AUD(s.loan_balance)}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num">{AUD(s.equity)}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num">{AUD(s.cumulative_cashflow)}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num">{AUD(s.baseline_value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
